/* ──────────────────────────────────────────────────────────────
 *  useExportJobs — Background export job orchestration hook
 *
 *  CHANGES:
 *   - Export trigger now gates on useJobStore.isGeometryReady for
 *     the exact (computeJobId, currentSelectedId) pair, not just on
 *     computeJobId + currentSelectedId being non-null. This closes
 *     the gap where switching to an already-generated sample fired
 *     export before that sample's own /mesh call had completed.
 *   - Job records are looked up/created via the composite
 *     jobKey(jobId, sampleId), matching useJobStore's new keying.
 *   - triggerExportGeneration now attaches expected_test_values,
 *     read from useSampleStore.expectedTestValuesBySample (populated
 *     by SampleTable), so the PDF/PPT export generators render the
 *     same "Expected Test Values" the UI showed for this sample.
 * ────────────────────────────────────────────────────────────── */

import { useCallback, useEffect, useRef } from 'react';

import {
  startExportJob,
  getJobStatus,
  refreshDownloadUrl,
} from '@/api/jobsApi';

import { useJobStore, jobKey } from '@/stores/useJobStore';
import { useSessionStore } from '@/stores/useSessionStore';
import { useBushingStore } from '@/stores/useBushingStore';
import { useInputStore } from '@/stores/useInputStore';
import { useConfigStore } from '@/stores/useConfigStore';
import { useSampleStore } from '@/stores/useSampleStore';

import type { ExportFileType } from '@/types/job';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;

const TERMINAL_JOB_STATUSES = [
  'completed',
  'partially_failed',
  'failed',
] as const;

function isTerminalJobStatus(status: string): boolean {
  return TERMINAL_JOB_STATUSES.some((s) => s === status);
}

export function useExportJobs() {
  const {
    computeJobId,
    currentJobId,
    currentSampleId,
    jobs,
    isPolling,
    updateJobFromResponse,
    setPolling,
    setCurrentJob,
  } = useJobStore();

  const sessionId = useSessionStore((state) => state.sessionId);

  const currentSelectedId = useSampleStore((state) => state.currentSelectedId);
  const genRowsById = useSampleStore((state) => state.genRowsById);

  // NEW — subscribe to the readiness map so the effect re-runs the
  // instant this specific (jobId, sampleId) pair flips to ready.
  const geometryReadyMap = useJobStore((s) => s.geometryReady);

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartRef = useRef<number>(0);
  const pollRequestInProgressRef = useRef(false);
  const lastTriggeredSampleIdRef = useRef<string | null>(null);

  const currentJob =
    currentJobId && currentSampleId
      ? jobs[jobKey(currentJobId, currentSampleId)] ?? null
      : null;

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollRequestInProgressRef.current = false;
    setPolling(false);
  }, [setPolling]);

  useEffect(() => {
    if (!currentJob) return;
    if (isTerminalJobStatus(currentJob.status)) {
      stopPolling();
    }
  }, [currentJob, stopPolling]);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      pollRequestInProgressRef.current = false;
    };
  }, []);

  const startPolling = useCallback(
    (jobId: string, sampleId: string) => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }

      pollRequestInProgressRef.current = false;
      pollStartRef.current = Date.now();
      setPolling(true);

      const poll = async () => {
        const elapsedTime = Date.now() - pollStartRef.current;

        if (elapsedTime > MAX_POLL_DURATION_MS) {
          console.warn(`[useExportJobs] Polling timed out for job ${jobId}/${sampleId}.`);
          stopPolling();
          return;
        }

        if (pollRequestInProgressRef.current) return;

        const state = useJobStore.getState();
        const stillActive =
          state.currentJobId === jobId && state.currentSampleId === sampleId;

        if (!stillActive) {
          stopPolling();
          return;
        }

        pollRequestInProgressRef.current = true;

        try {
          // Backend now keys job state by (job_id, sample_id) — see
          // job_manager.py fix. getJobStatus must send both.
          const status = await getJobStatus(jobId, sampleId);
          updateJobFromResponse(status);

          if (isTerminalJobStatus(status.status)) {
            stopPolling();
          }
        } catch (error) {
          console.error(`[useExportJobs] Poll failed for job ${jobId}/${sampleId}:`, error);
        } finally {
          pollRequestInProgressRef.current = false;
        }
      };

      void poll();
      pollTimerRef.current = setInterval(() => {
        void poll();
      }, POLL_INTERVAL_MS);
    },
    [setPolling, stopPolling, updateJobFromResponse],
  );

  const triggerExportGeneration = useCallback(
    async (jobId: string, sampleId: string) => {
      if (!sessionId) {
        console.error('[useExportJobs] Cannot start export because sessionId is missing.');
        if (useSampleStore.getState().currentSelectedId === sampleId) {
          lastTriggeredSampleIdRef.current = null;
        }
        return;
      }

      const { bushingType } = useBushingStore.getState();

      const {
        packaging,
        target,
        sleeveThickness,
        shoreA,
        compoundFamily,
        sorString,
        calculatedMinContactArea,
      } = useInputStore.getState();

      const {
        coreMaterial,
        outerMaterial,
        interSleeveMaterial,
        weightPockets,
        stiffnessPriorities,
        clampLoad,
        allowableYield,
        coreIdCalculator,
        sealRadial,
        minContactArea,
      } = useConfigStore.getState();

      const { genRowsById: latestGenRowsById, expectedTestValuesBySample } =
        useSampleStore.getState();
      const sample = latestGenRowsById[sampleId];

      if (!sample) {
        console.error('[useExportJobs] Generated sample not found:', sampleId);
        if (useSampleStore.getState().currentSelectedId === sampleId) {
          lastTriggeredSampleIdRef.current = null;
        }
        return;
      }

      // Cached by SampleTable as soon as the FEA→test inverse solver
      // (A1/B1/C1) or design-value passthrough (D1/BYD) settles for
      // this sample. If export fires before that cache entry exists —
      // e.g. geometry became ready faster than the solver call — this
      // is null and the PDF/PPT generators render blank Expected Test
      // Value cells rather than blocking export on it.
      const expectedTestValues = expectedTestValuesBySample[sampleId] ?? null;

      const inputParameters = {
        bushingType,
        sorString,
        coreLength: packaging.coreLength,
        outerSleeveLength: packaging.outerSleeveLength,
        controlArmId: packaging.controlArmId,
        coreId: packaging.coreId,
        coreOdMin: packaging.coreOdMin,
        coreOdMax: packaging.coreOdMax,
        hotOd: packaging.hotOd,
        collarOd: packaging.collarOd,
        sleeveThickness,
        shoreA,
        compoundFamily,
        targets: target.targets,
        amplitudes: target.amplitudes,
        linearTravel: target.linearTravel,
        coreMaterial,
        outerMaterial,
        interSleeveMaterial,
        weightPockets,
        stiffnessPriorities,
        clampLoad,
        allowableYield,
        coreIdCalculator,
        sealRadial,
        minContactArea,
        calculatedMinContactArea,
      };

      try {
        await startExportJob({
          session_id: sessionId,
          job_id: jobId,
          sample_id: sampleId,
          bushing_type: bushingType,
          sample: sample as Record<string, unknown>,
          core_id: packaging.coreId,
          sor_string: sorString,
          shore_a: shoreA,
          linear_travel_radial_plus: target.linearTravel.radial_plus ?? null,
          linear_travel_radial_minus: target.linearTravel.radial_minus ?? null,
          input_parameters: inputParameters,
          expected_test_values: expectedTestValues,
        });

        const latestSelectedId = useSampleStore.getState().currentSelectedId;
        const isStillCurrentSample = latestSelectedId === sampleId;

        useJobStore.getState().createJob(jobId, sessionId, bushingType, sampleId, isStillCurrentSample);

        if (isStillCurrentSample) {
          startPolling(jobId, sampleId);
        }
      } catch (error) {
        console.error(`[useExportJobs] Failed to start export job for sample ${sampleId}:`, error);

        if (useSampleStore.getState().currentSelectedId === sampleId) {
          lastTriggeredSampleIdRef.current = null;
          useJobStore.getState().setCurrentJob(null);
        }
      }
    },
    [sessionId, startPolling],
  );

  // ── Watch selected sample and generate exports ──────────────
  useEffect(() => {
    if (!computeJobId) {
      lastTriggeredSampleIdRef.current = null;
      stopPolling();
      setCurrentJob(null);
      return;
    }

    if (!currentSelectedId) {
      lastTriggeredSampleIdRef.current = null;
      stopPolling();
      setCurrentJob(null);
      return;
    }

    const selectedSample = genRowsById[currentSelectedId];

    if (!selectedSample) {
      stopPolling();
      setCurrentJob(null);
      return;
    }

    // NEW GATE (fixes #2): don't trigger until THIS sample's own
    // geometry/job_state is confirmed to exist on the backend. This
    // effect re-runs automatically when geometryReadyMap updates
    // (it's a dependency below), so as soon as useGeometry's or
    // doCompute's /mesh call succeeds for this pair, export fires.
    const readyKey = jobKey(computeJobId, currentSelectedId);
    if (!geometryReadyMap[readyKey]) {
      return;
    }

    if (lastTriggeredSampleIdRef.current === currentSelectedId) {
      return;
    }

    lastTriggeredSampleIdRef.current = currentSelectedId;

    stopPolling();
    setCurrentJob(null);

    void triggerExportGeneration(computeJobId, currentSelectedId);
  }, [
    computeJobId,
    currentSelectedId,
    genRowsById,
    geometryReadyMap,
    setCurrentJob,
    stopPolling,
    triggerExportGeneration,
  ]);

  const downloadFile = useCallback(
    async (fileType: ExportFileType) => {
      if (!currentJobId || !currentJob) return;
      const fileInfo = currentJob.files[fileType];
      if (!fileInfo || fileInfo.status !== 'ready' || !fileInfo.download_url) return;
      window.open(fileInfo.download_url, '_blank', 'noopener,noreferrer');
    },
    [currentJobId, currentJob],
  );

  const refreshUrl = useCallback(
    async (fileType: ExportFileType) => {
      if (!currentJobId || !currentJob) return null;
      const fileInfo = currentJob.files[fileType];
      if (!fileInfo) return null;

      try {
        const result = await refreshDownloadUrl(
          currentJob.sessionId,
          currentJobId,
          currentJob.sampleId,
          fileInfo.filename,
        );

        useJobStore.getState().updateFileDownloadUrl(
          currentJobId,
          currentJob.sampleId,
          fileType,
          result.download_url,
        );

        return result.download_url;
      } catch (error) {
        console.error('[useExportJobs] URL refresh failed:', error);
        return null;
      }
    },
    [currentJobId, currentJob],
  );

  const currentJobFiles = currentJob ? Object.values(currentJob.files) : [];

  const allFilesReady =
    currentJob !== null &&
    currentJobFiles.length > 0 &&
    currentJobFiles.every((f) => f.status === 'ready' || f.status === 'failed');

  const readyFileCount = currentJobFiles.filter((f) => f.status === 'ready').length;
  const totalFileCount = currentJobFiles.length;

  return {
    currentJob,
    currentJobId,
    isPolling,
    allFilesReady,
    readyFileCount,
    totalFileCount,
    triggerExportGeneration,
    downloadFile,
    refreshUrl,
    stopPolling,
  };
}